<template>
  <div class="answer" :class="{ 'answer-missing': unanswered }">
    <div class="answer-head">
      <span class="code">{{ question.code }}</span>
      <span v-if="question.isRequired" class="chip chip-required">Requerida</span>
      <span v-if="question.isProfile" class="chip chip-grey">Perfil</span>
    </div>
    <div class="text">{{ question.text }}</div>
    <div v-if="question.helperText" class="helper">{{ question.helperText }}</div>

    <div v-if="showRadio" class="options-row">
      <label
        v-for="opt in question.options"
        :key="opt._id"
        class="opt"
        :class="['opt-' + (opt.color || 'gray'), { selected: selectedId === opt._id }]"
      >
        <input
          type="radio"
          :name="question._id"
          :value="opt._id"
          v-model="selectedId"
          :disabled="disabled"
        />
        {{ opt.label }}
        <span class="opt-value">({{ opt.value }})</span>
      </label>
    </div>

    <div v-else-if="question.fieldType === 'MULTIPLE' && question.allowMultiple" class="options-col">
      <label
        v-for="opt in question.options"
        :key="opt._id"
        class="opt opt-check"
        :class="['opt-' + (opt.color || 'gray'), { selected: selectedIds.includes(opt._id) }]"
      >
        <input
          type="checkbox"
          :value="opt._id"
          v-model="selectedIds"
          :disabled="disabled"
        />
        {{ opt.label }}
        <span class="opt-value">({{ opt.value }})</span>
      </label>
    </div>

    <div v-else-if="question.fieldType === 'TEXT'" class="field">
      <input
        type="text"
        v-model="valueRaw"
        :disabled="disabled"
        placeholder="Respuesta"
        class="input"
      />
    </div>

    <div v-else-if="question.fieldType === 'NUMBER'" class="field">
      <input
        type="number"
        v-model.number="valueRaw"
        :disabled="disabled"
        placeholder="Respuesta numérica"
        class="input"
      />
    </div>

    <div v-else-if="question.fieldType === 'DATE'" class="field">
      <input
        type="date"
        v-model="valueRaw"
        :disabled="disabled"
        class="input"
      />
    </div>

    <div v-else-if="question.fieldType === 'SCALE'" class="field">
      <div class="scale-row">
        <span class="scale-min">0</span>
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          v-model.number="valueRaw"
          :disabled="disabled"
          class="scale"
        />
        <span class="scale-val">{{ valueRaw != null ? valueRaw : 0 }}</span>
        <span class="scale-max">100</span>
      </div>
    </div>

    <div class="field">
      <input
        type="text"
        v-model="comment"
        :disabled="disabled"
        placeholder="Comentario (opcional)"
        class="input"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'SacResponseAnswer',
  props: {
    question: { type: Object, required: true },
    answer: { type: Object, default: () => ({}) },
    disabled: { type: Boolean, default: false },
    unanswered: { type: Boolean, default: false },
  },
  data() {
    const opt = this.answer && this.answer.selectedOption;
    const optId = opt ? (opt._id || opt.id || opt) : null;
    const opts = this.answer && this.answer.selectedOptions;
    const optIds = Array.isArray(opts) ? opts.map((o) => (o && o._id) || o) : [];
    return {
      selectedId: optId,
      selectedIds: optIds,
      valueRaw: this.answer && this.answer.valueRaw != null ? this.answer.valueRaw : null,
      comment: this.answer && this.answer.comment ? this.answer.comment : '',
    };
  },
  computed: {
    showRadio() {
      return ['YES_NO', 'LIKERT'].includes(this.question.fieldType) ||
        (this.question.fieldType === 'MULTIPLE' && this.question.options && this.question.options.length > 0 && !this.question.allowMultiple);
    },
  },
  watch: {
    selectedId() { this.emit(); },
    selectedIds() { this.emit(); },
    valueRaw() { this.emit(); },
    comment() { this.emit(); },
  },
  methods: {
    emit() {
      this.$emit('change', {
        questionId: this.question._id,
        selectedOptionId: this.selectedId || undefined,
        selectedOptions: this.selectedIds.length ? this.selectedIds : undefined,
        valueRaw: this.valueRaw !== null && this.valueRaw !== '' ? this.valueRaw : undefined,
        comment: this.comment || undefined,
      });
    },
  },
};
</script>

<style scoped>
.answer {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 14px;
  margin-bottom: 12px;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.answer-missing {
  border-color: #d32f2f;
  box-shadow: 0 0 0 1px #d32f2f inset;
  background: #fff5f5;
}
.answer-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}
.code {
  font-weight: 700;
  font-size: 0.85rem;
  color: #333;
}
.text {
  font-size: 0.95rem;
  margin-bottom: 4px;
  color: #222;
}
.helper {
  font-size: 0.8rem;
  color: #757575;
  margin-bottom: 8px;
}
.chip {
  display: inline-block;
  padding: 2px 8px;
  font-size: 0.7rem;
  border-radius: 10px;
  font-weight: 500;
  line-height: 1.4;
}
.chip-required {
  background: #ffebee;
  color: #b71c1c;
}
.chip-grey {
  background: #eceff1;
  color: #37474f;
}
.options-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.options-col {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}
.opt {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  border: 1px solid transparent;
  transition: border 0.15s, background 0.15s;
  user-select: none;
}
.opt input {
  margin: 0;
  cursor: pointer;
}
.opt-value {
  color: #555;
  font-size: 0.78rem;
  margin-left: 2px;
}
.opt-green { background: #e8f5e9; color: #1b5e20; }
.opt-red { background: #ffebee; color: #b71c1c; }
.opt-yellow { background: #fff8e1; color: #f57f17; }
.opt-gray { background: #eceff1; color: #37474f; }
.opt.selected {
  border-color: #1976d2;
  box-shadow: 0 0 0 1px #1976d2;
}
.opt-check {
  align-self: flex-start;
}
.field {
  margin-top: 8px;
}
.input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #c4c4c4;
  border-radius: 4px;
  font-size: 0.9rem;
  outline: none;
  transition: border 0.15s;
  box-sizing: border-box;
}
.input:focus {
  border-color: #1976d2;
}
.input:disabled {
  background: #f5f5f5;
  cursor: not-allowed;
}
.scale-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
}
.scale {
  flex: 1;
  accent-color: #1976d2;
}
.scale-min, .scale-max {
  font-size: 0.8rem;
  color: #757575;
}
.scale-val {
  min-width: 32px;
  text-align: center;
  font-weight: 600;
  color: #1976d2;
}
</style>
